import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  MdAddCircleOutline,
  MdDeleteOutline,
  MdOutlineEdit
} from 'react-icons/md'

import useFollowUpConfigComprador from '@hook/followUpFornecedor/use-config-comprador'
import useFilters from '@hook/shared/use-filters'
import useLayoutHeight from '@hook/shared/use-layout-height'
import ConfigCompradorProvider from '@provider/FollowUpFornecedor/ConfigCompradorProvider'

import HistoricoResponsaveisModal from '@component/FollowUpFornecedor/HistoricoResponsaveisModal'
import MultiItemCell from '@component/FollowUpFornecedor/MultiItemCell'
import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutLoader from '@component/Layout/Loader'
import LayoutTools from '@component/Layout/Tools'

import { IConfigCompradorItem } from '@base/@types/providers/FollowUpFornecedor/ConfigCompradorProvider'

import { parseCSVLine } from '../../../utils/followUpFornecedor/csv'
import ImportModal, { IImportRow } from './ImportModal'
import ManutencaoModal from './ManutencaoModal'

// ConfigComprador normalizeHeader strips all non-alphanumeric chars (different from the csv.ts version)
function normalizeHeaderCC(h: string): string {
  return h
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim()
}

type SelectOption = { value: string | number; label: string }

const ConfigCompradorContent: FC = () => {
  const height = useLayoutHeight()
  const { loading, data, getData, remove } = useFollowUpConfigComprador()
  const toast = useToast()

  const {
    providersForPurchase,
    compactCategories,
    users,
    getFilters,
    loading: loadingFilters
  } = useFilters()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editItem, setEditItem] = useState<IConfigCompradorItem | null>(null)
  const [importRows, setImportRows] = useState<IImportRow[]>([])
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [historicoOpen, setHistoricoOpen] = useState(false)

  useEffect(() => {
    getData()
    getFilters({
      providersForPurchase: true,
      compactCategories: true,
      users: true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const supplierOptions = useMemo(
    () =>
      providersForPurchase?.map((s: any) => ({
        value: Number(s.value),
        label: s.label
      })) ?? [],
    [providersForPurchase]
  )

  const categoryOptions = useMemo(
    () =>
      compactCategories?.map((c: any) => ({
        value: Number(c.value),
        label: c.label
      })) ?? [],
    [compactCategories]
  )

  const userOptions = useMemo(
    () => users?.map((u: any) => ({ value: u.value, label: u.label })) ?? [],
    [users]
  )

  const filtered = useMemo(
    () =>
      query
        ? data.filter(
            i =>
              i.fornecedorNome.toLowerCase().includes(query.toLowerCase()) ||
              String(i.fornecedorId).includes(query) ||
              i.usuarios.some(u =>
                u.toLowerCase().includes(query.toLowerCase())
              ) ||
              (i.categoriaCompactaNome ?? '')
                .toLowerCase()
                .includes(query.toLowerCase())
          )
        : data,
    [data, query]
  )

  const openCreate = () => {
    setEditItem(null)
    setModalOpen(true)
  }

  const openEdit = (item: IConfigCompradorItem) => {
    setEditItem(item)
    setModalOpen(true)
  }

  const handleExport = () => {
    const header =
      'FornecedorId;FornecedorNome;CategoriaCompactaId;CategoriaCompactaNome;Email'
    const rows = data.flatMap(item =>
      item.usuarios.map(email =>
        [
          item.fornecedorId,
          `"${item.fornecedorNome}"`,
          item.categoriaCompactaId ?? '',
          item.categoriaCompactaNome ? `"${item.categoriaCompactaNome}"` : '',
          email
        ].join(';')
      )
    )
    fileDownload(
      '\uFEFF' + [header, ...rows].join('\r\n'),
      'config-compradores-followup.csv',
      'text/csv;charset=utf-8'
    )
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = evt => {
      let text = (evt.target?.result as string) ?? ''
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)
      const lines = text.split(/\r?\n/).filter(l => l.trim())
      if (lines.length < 2) {
        toast({
          title: 'Arquivo inválido ou vazio',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right'
        })
        return
      }
      const sep = lines[0].includes(';') ? ';' : ','
      const headers = parseCSVLine(lines[0], sep).map(normalizeHeaderCC)

      const idxFornecedorId = headers.findIndex(h => h === 'fornecedorid')
      const idxFornecedorNome = headers.findIndex(h => h === 'fornecedornome')
      const idxCatId = headers.findIndex(h => h === 'categoriacompactaid')
      const idxCatNome = headers.findIndex(h => h === 'categoriacompactanome')
      const idxEmail = headers.findIndex(h => h === 'email')

      if (idxFornecedorId === -1 || idxEmail === -1) {
        toast({
          title: 'CSV inválido: colunas FornecedorId e Email são obrigatórias',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'bottom-right'
        })
        return
      }

      const parsed: IImportRow[] = []
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i], sep)
        const supplierId = Number(cols[idxFornecedorId])
        const email = cols[idxEmail]
        if (!supplierId || !email) continue
        const rawCatId = idxCatId >= 0 ? cols[idxCatId] : ''
        parsed.push({
          supplierId,
          fornecedorNome: idxFornecedorNome >= 0 ? cols[idxFornecedorNome] : '',
          categoriaCompactaId: rawCatId ? Number(rawCatId) : null,
          categoriaCompactaNome:
            idxCatNome >= 0 ? cols[idxCatNome] || null : null,
          email
        })
      }

      if (parsed.length === 0) {
        toast({
          title: 'Nenhuma linha válida encontrada no CSV',
          status: 'warning',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right'
        })
        return
      }

      setImportRows(parsed)
      setImportModalOpen(true)
    }
    reader.readAsText(file, 'UTF-8')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const actions = useMemo(
    () => [
      {
        type: 'button' as const,
        label: 'Importar CSV',
        onClick: () => fileInputRef.current?.click()
      },
      {
        type: 'button' as const,
        label: 'Histórico',
        onClick: () => setHistoricoOpen(true)
      },
      {
        type: 'export' as const,
        exports: [
          { type: 'excel' as const, data: filtered, onClick: handleExport }
        ]
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered]
  )

  return (
    <Layout title="Follow Up Fornecedor — Config. Compradores">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Grid w="100vw" bg="white" h={height} templateColumns="100vw">
        <Box>
          <LayoutTools
            title="Config. Compradores — Follow Up"
            actions={actions}
            query={query}
            onSearchChange={setQuery}
            isDisabled={loading || data.length === 0}
          />

          <Flex
            borderBottom="solid 1px"
            borderColor="gray.200"
            alignItems="center"
            px={5}
            py={3}
          >
            <Spacer />
            <Button
              size="md"
              bg="#1d5840"
              color="white"
              fontWeight="600"
              px={5}
              leftIcon={<Icon as={MdAddCircleOutline} boxSize={5} />}
              borderRadius="lg"
              _hover={{ bg: '#174a35' }}
              _active={{ bg: '#123c2b' }}
              onClick={openCreate}
            >
              Nova Configuração
            </Button>
          </Flex>

          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros"
            />
          ) : data.length === 0 ? (
            <LayoutEmpty
              title="Nenhuma configuração encontrada"
              message="Clique em '+ Nova Configuração' para começar"
            />
          ) : (
            <Box overflow="auto" px={5} py={4}>
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
              >
                <Table size="sm" className="scl-table-grid">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Fornecedor</Th>
                      <Th>Categoria Compacta</Th>
                      <Th>Usuários Responsáveis</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filtered.map(item => (
                      <Tr key={item.id}>
                        <Td>
                          <Text fontWeight="500" fontSize="13px">
                            {item.fornecedorNome}
                          </Text>
                          <Text fontSize="11px" color="gray.400">
                            ID {item.fornecedorId}
                          </Text>
                        </Td>
                        <Td>
                          {item.categoriaCompactaId ? (
                            <Badge
                              bg="blue.600"
                              color="white"
                              borderRadius="full"
                              fontSize="11px"
                              px={2}
                            >
                              {item.categoriaCompactaNome ??
                                `ID ${item.categoriaCompactaId}`}
                            </Badge>
                          ) : (
                            <Badge
                              bg="green.600"
                              color="white"
                              borderRadius="full"
                              fontSize="11px"
                              px={2}
                            >
                              Todos os produtos
                            </Badge>
                          )}
                        </Td>
                        <Td>
                          <MultiItemCell items={item.usuarios} />
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="Editar" hasArrow placement="top">
                              <IconButton
                                aria-label="Editar"
                                icon={<Icon as={MdOutlineEdit} boxSize={4} />}
                                size="xs"
                                bg="blue.500"
                                color="white"
                                _hover={{ bg: 'blue.600' }}
                                _active={{ bg: 'blue.700' }}
                                onClick={() => openEdit(item)}
                              />
                            </Tooltip>
                            <Tooltip label="Remover" hasArrow placement="top">
                              <IconButton
                                aria-label="Remover"
                                icon={<Icon as={MdDeleteOutline} boxSize={4} />}
                                size="xs"
                                bg="red.500"
                                color="white"
                                _hover={{ bg: 'red.600' }}
                                _active={{ bg: 'red.700' }}
                                onClick={() => remove(item.id)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>

      <HistoricoResponsaveisModal
        isOpen={historicoOpen}
        onClose={() => setHistoricoOpen(false)}
      />

      <ManutencaoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        editItem={editItem}
        supplierOptions={supplierOptions}
        categoryOptions={categoryOptions}
        userOptions={userOptions}
        loadingFilters={loadingFilters}
      />

      <ImportModal
        isOpen={importModalOpen}
        rows={importRows}
        onClose={() => setImportModalOpen(false)}
        onSuccess={getData}
      />
    </Layout>
  )
}

const FollowUpFornecedorConfigComprador: FC = () => (
  <ConfigCompradorProvider>
    <ConfigCompradorContent />
  </ConfigCompradorProvider>
)

export default FollowUpFornecedorConfigComprador
